import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { MaskedId } from "./AccountDetails";
import { formatUserType, safeDateTime } from "../../../utils";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";

const OrganizationDetails = ({ organization, members }) => {
  const theme = useTheme();
  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.divider, 0.14),
          p: 2,
        }}
      >
        <Stack spacing={1}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <BusinessIcon fontSize='small' color='action' />
            <Typography fontWeight={900}>Organization</Typography>
          </Stack>

          {!organization ? (
            <Typography variant='body2' color='text.secondary'>
              No organization found on this subscription.
            </Typography>
          ) : (
            <Stack spacing={1}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    fontWeight={800}
                  >
                    Name
                  </Typography>
                  <Typography variant='body2' fontWeight={700} noWrap>
                    {organization?.name || "—"}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <MaskedId label='Organization ID' value={organization?.id} />
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    fontWeight={800}
                  >
                    GST registration no.
                  </Typography>
                  <Typography variant='body2' color='text.secondary' noWrap>
                    {organization?.GST_registration_no || "—"}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <MaskedId
                    label='Created by'
                    value={organization?.created_by}
                  />
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    fontWeight={800}
                  >
                    Created
                  </Typography>
                  <Typography variant='body2' color='text.secondary' noWrap>
                    {safeDateTime(organization?.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    fontWeight={800}
                  >
                    Updated
                  </Typography>
                  <Typography variant='body2' color='text.secondary' noWrap>
                    {safeDateTime(organization?.updated_at)}
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={800}
                >
                  Description
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {organization?.description || "—"}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.divider, 0.14),
          p: 2,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <GroupsIcon fontSize='small' color='action' />
            <Typography fontWeight={900}>Members ({members.length})</Typography>
          </Stack>

          {members.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              No members found on this subscription.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {members.map((m) => {
                const auth = m?.user_auth || null;
                const memberName = m?.name || "—";
                const memberEmail = m?.email || "—";
                const memberRole = auth?.user_role || "—";
                const memberType = formatUserType(auth?.user_type);
                const memberStatus = auth?.status || "—";

                return (
                  <Box
                    key={m?.u_id || memberEmail}
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: (t) => alpha(t.palette.divider, 0.18),
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent='space-between'
                    >
                      <Stack direction='row' spacing={1.25} alignItems='center'>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: alpha(theme.palette.primary.main, 0.18),
                            color: "primary.main",
                            fontWeight: 900,
                          }}
                        >
                          {(
                            memberName?.[0] ||
                            memberEmail?.[0] ||
                            "?"
                          ).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={800} noWrap>
                            {memberName}
                          </Typography>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            noWrap
                          >
                            {memberEmail}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        flexWrap='wrap'
                      >
                        <Chip
                          size='small'
                          label={memberType || "—"}
                          variant='outlined'
                          sx={{ fontWeight: 700 }}
                        />
                        <Chip
                          size='small'
                          label={`Role: ${memberRole}`}
                          variant='outlined'
                          sx={{ fontWeight: 700 }}
                        />
                        <Chip
                          size='small'
                          label={memberStatus}
                          color={
                            memberStatus === "active" ? "success" : "default"
                          }
                          sx={{ fontWeight: 800 }}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default OrganizationDetails;
